import { useState } from 'react';

import { Modal } from '@/components/Modal';
import { CapabilitySchedule } from '@/hooks/useCapabilitySchedule';

interface DeleteScheduleModalProps {
  schedule: CapabilitySchedule | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (scheduleId: string) => Promise<void>;
}

/**
 * DeleteScheduleModal - Confirmation modal for schedule deletion
 *
 * Displays a warning message and confirmation button for deleting
 * a capability schedule. Handles loading state during deletion.
 */
export function DeleteScheduleModal({
  schedule,
  isOpen,
  onClose,
  onConfirm,
}: DeleteScheduleModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!schedule) return;
    setIsDeleting(true);
    try {
      await onConfirm(schedule.scheduleId);
      onClose();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!schedule) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Delete Schedule"
      size="lg"
      footer={{
        text: isDeleting ? 'Deleting...' : 'Delete',
        onClick: handleDelete,
        disabled: isDeleting,
        styleType: 'primary',
      }}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div>
            <p className="mb-1 text-sm font-medium text-red-800 dark:text-red-200">
              Warning: This action cannot be undone
            </p>
            <p className="text-sm text-red-700 dark:text-red-300">
              Deleting this schedule will permanently remove it and stop all
              future scheduled executions.
            </p>
          </div>
        </div>
        <div>
          <p className="text-chariot-text-primary text-sm">
            Are you sure you want to delete the schedule for{' '}
            <b>{schedule.capabilityName}</b>?
          </p>
        </div>
      </div>
    </Modal>
  );
}
